import { useAvailableExternalAuthenticationsLazyQuery } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { getAppMountUriForRedirect } from "@dashboard/utils/urls";
import React, { useEffect, useRef } from "react";
import urlJoin from "url-join";
import useRouter from "use-react-router";

import { useUser } from "..";
import LoginLoading from "../components/LoginLoading";
import LoginPage from "../components/LoginPage";
import { LoginFormData } from "../components/LoginPage/types";
import { useAuthParameters } from "../hooks/useAuthParameters";
import { isServedPath } from "../servedPaths";
import { loginCallbackPath, LoginUrlQueryParams } from "../urls";
import {
  clearSignedOut,
  startPkce,
  takePkceVerifier,
  wasSignedOut,
  WSM_CLERK_PLUGIN_ID,
} from "../wsmClerk";
import WsmSignIn from "./WsmSignIn";

interface LoginViewProps {
  params: LoginUrlQueryParams;
}

const LoginView = ({ params }: LoginViewProps) => {
  const navigate = useNavigator();
  const { location } = useRouter();
  const {
    login,
    requestLoginByExternalPlugin,
    loginByExternalPlugin,
    authenticating,
    errors,
    loginMessage,
  } = useUser();
  const [
    queryExternalAuthentications,
    { data: externalAuthentications, loading: externalAuthenticationsLoading, called },
  ] = useAvailableExternalAuthenticationsLazyQuery();
  const {
    fallbackUri,
    requestedExternalPluginId,
    isCallbackPath,
    setFallbackUri,
    setRequestedExternalPluginId,
  } = useAuthParameters();
  const handleSubmit = async (data: LoginFormData) => {
    if (!login) {
      return;
    }

    const result = await login(data.email, data.password);
    const errors = result?.errors || [];

    return errors;
  };
  const handleRequestExternalAuthentication = async (pluginId: string) => {
    // Back to the page that asked, when the signed-in app serves it; a password page
    // or a stale bookmark has no route there and would land on a 404 (servedPaths.ts).
    setFallbackUri(
      isServedPath(location.pathname) ? location.pathname + location.search : "/",
    );

    const redirectUri = urlJoin(
      window.location.origin,
      getAppMountUriForRedirect(),
      loginCallbackPath,
    );
    // The Web Shop Manager sign-in binds the code to this tab (PKCE); a stock provider
    // gets the stock input.
    const input =
      pluginId === WSM_CLERK_PLUGIN_ID
        ? { redirectUri, codeChallenge: await startPkce() }
        : { redirectUri };
    const result = await requestLoginByExternalPlugin!(pluginId, input);
    const data = JSON.parse(result?.authenticationData || "null");

    if (data && !result?.errors?.length) {
      setRequestedExternalPluginId(pluginId);
      clearSignedOut();
      window.location.href = data.authorizationUrl;
    }
  };
  const handleExternalAuthentication = async (code: string, state: string) => {
    // The Web Shop Manager tenant's plugin id is a constant, so the callback does not
    // depend on the localStorage note a different tab or a cleared storage may have lost.
    const pluginId = requestedExternalPluginId ?? WSM_CLERK_PLUGIN_ID;
    const codeVerifier = pluginId === WSM_CLERK_PLUGIN_ID ? takePkceVerifier() : null;

    await loginByExternalPlugin!(pluginId, {
      code,
      state,
      ...(codeVerifier ? { codeVerifier } : {}),
    });
    setRequestedExternalPluginId(null);
    navigate(fallbackUri);
    setFallbackUri(null);
  };

  const { code, state, error: providerError, error_description: providerErrorDescription } =
    params;
  const externalAuthParamsExist = !!(code && state && isCallbackPath);
  const providerRefused = !!(providerError && isCallbackPath);

  useEffect(() => {
    if (!externalAuthParamsExist) {
      queryExternalAuthentications();
    }
  }, [externalAuthParamsExist, queryExternalAuthentications]);
  useEffect(() => {
    const externalAuthNotPerformed = !authenticating && !errors.length;

    if (externalAuthParamsExist && externalAuthNotPerformed) {
      handleExternalAuthentication(code!, state!);
    }

    return () => {
      setRequestedExternalPluginId(null);
      setFallbackUri(null);
    };
  }, []);

  const wsmSignIn = (externalAuthentications?.shop?.availableExternalAuthentications ?? []).some(
    auth => auth.id === WSM_CLERK_PLUGIN_ID,
  );
  const startWsmSignIn = () => handleRequestExternalAuthentication(WSM_CLERK_PLUGIN_ID);
  // A Web Shop Manager tenant with nothing stopping it goes straight to Clerk: no page
  // of ours in between. Once per mount, so a slow redirect does not fire twice.
  const redirecting = useRef(false);
  const stopped = wasSignedOut() || errors.length > 0 || !!loginMessage || providerRefused;

  useEffect(() => {
    if (wsmSignIn && !stopped && !externalAuthParamsExist && !redirecting.current) {
      redirecting.current = true;
      startWsmSignIn();
    }
  }, [wsmSignIn, stopped, externalAuthParamsExist]);

  if (externalAuthParamsExist && !errors.length && !loginMessage) {
    // Finishing a sign-in: nothing to show until the tenant has answered.
    return <LoginLoading />;
  }

  if (wsmSignIn) {
    if (!stopped) {
      return <LoginLoading />;
    }

    return (
      <WsmSignIn
        errors={errors}
        message={loginMessage || providerErrorDescription || providerError || null}
        disabled={authenticating}
        onSignIn={startWsmSignIn}
      />
    );
  }

  if (!called || (externalAuthenticationsLoading && !externalAuthentications)) {
    // Until the tenant has answered, no door: a password form shown first and swapped
    // out would still be a password form on a Web Shop Manager tenant.
    return <LoginLoading />;
  }

  return (
    <LoginPage
      errors={errors}
      disabled={authenticating}
      externalAuthentications={externalAuthentications?.shop?.availableExternalAuthentications}
      loading={externalAuthenticationsLoading || authenticating}
      onExternalAuthentication={handleRequestExternalAuthentication}
      onSubmit={handleSubmit}
    />
  );
};

LoginView.displayName = "LoginView";
export default LoginView;
